import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowLeft, ArrowRight, Euro, TrendingUp, Hammer } from 'lucide-react';
import { QuestionCard } from './QuestionCard';
import { ProgressIndicator } from './ProgressIndicator';
import { QuestionsSummary } from './QuestionsSummary';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { DPEAPI } from '@/services/api';
import { QuestionConditionEvaluator } from '@/utils/questionConditions';
import type { ContextQuestion, QuestionAnswer } from '@/types/frontend';
import { analytics } from '@/utils/analytics';
import { metaPixel } from '@/utils/meta-pixel';

interface QuestionsFlowProps {
  dpeId: string;
  analysisId?: string;
  questionnaireId?: string;
  onComplete?: (recommendations: any) => void;
}

export const QuestionsFlow: React.FC<QuestionsFlowProps> = ({
  dpeId,
  analysisId,
  questionnaireId,
  onComplete
}) => {
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState<ContextQuestion[]>([]);
  const [answers, setAnswers] = useState<QuestionAnswer[]>([]);
  const [visibleQuestions, setVisibleQuestions] = useState<ContextQuestion[]>([]);
  const [dpeData, setDpeData] = useState<any>(null);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(questionnaireId || null);
  const [showSummary, setShowSummary] = useState(false);
  const [recommendations, setRecommendations] = useState<any | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const completionFired = useRef(false);
  
  // Load questions
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // If we have a questionnaire ID, load existing session
        if (questionnaireId) {
          // In a real app, we would load the existing session
          // For now, we'll just load new questions
          const response = await DPEAPI.getQuestions(dpeId, analysisId);
          
          console.log('=== DEBUG_FRONTEND_QUESTIONS_FLOW: Questions loaded ===');
          console.log('DEBUG_FRONTEND_QUESTIONS_FLOW: Response from backend:', {
            questionsCount: response.questions?.length || 0,
            questionIds: response.questions?.map(q => q.id) || [],
            questionCategories: response.questions?.map(q => q.category) || [],
            hasDpeData: !!response.dpe_data,
            hasAnalysisData: !!response.analysis_data
          });
          
          setQuestions(response.questions);
          
          // ✅ NOUVEAU : Récupérer aussi les données DPE et d'analyse pour l'évaluateur
          if (response.dpe_data) {
            console.log('DEBUG_FRONTEND_QUESTIONS_FLOW: Setting DPE data:', {
              dpeDataKeys: Object.keys(response.dpe_data),
              qualite_isolation_plancher_haut_comble_perdu: response.dpe_data.qualite_isolation_plancher_haut_comble_perdu,
              isolation_toiture: response.dpe_data.isolation_toiture,
              deperditions_planchers_hauts: response.dpe_data.deperditions_planchers_hauts,
              etiquette_dpe: response.dpe_data.etiquette_dpe
            });
            setDpeData(response.dpe_data);
          }
          if (response.analysis_data) {
            console.log('DEBUG_FRONTEND_QUESTIONS_FLOW: Setting analysis data:', {
              analysisDataKeys: Object.keys(response.analysis_data),
              factorsAnalysisKeys: response.analysis_data.factors_analysis ? Object.keys(response.analysis_data.factors_analysis) : 'no factors_analysis',
              errorsDetectedCount: response.analysis_data.errors_detected?.length || 0,
              opportunitiesCount: response.analysis_data.opportunities?.length || 0
            });
            setAnalysisData(response.analysis_data);
          }
          
          setSessionId(response.session_id);
          
          // TODO: Load existing answers if available
        } else {
          // Load new questions
          const response = await DPEAPI.getQuestions(dpeId, analysisId);
          
          console.log('=== DEBUG_FRONTEND_QUESTIONS_FLOW: New questions loaded ===');
          console.log('DEBUG_FRONTEND_QUESTIONS_FLOW: Response from backend:', {
            questionsCount: response.questions?.length || 0,
            questionIds: response.questions?.map(q => q.id) || [],
            questionCategories: response.questions?.map(q => q.category) || [],
            hasDpeData: !!response.dpe_data,
            hasAnalysisData: !!response.analysis_data
          });
          
          setQuestions(response.questions);
          
          // ✅ NOUVEAU : Récupérer aussi les données DPE et d'analyse
          if (response.dpe_data) {
            console.log('DEBUG_FRONTEND_QUESTIONS_FLOW: Setting DPE data:', {
              dpeDataKeys: Object.keys(response.dpe_data),
              qualite_isolation_plancher_haut_comble_perdu: response.dpe_data.qualite_isolation_plancher_haut_comble_perdu,
              isolation_toiture: response.dpe_data.isolation_toiture,
              deperditions_planchers_hauts: response.dpe_data.deperditions_planchers_hauts,
              etiquette_dpe: response.dpe_data.etiquette_dpe
            });
            setDpeData(response.dpe_data);
          }
          if (response.analysis_data) {
            console.log('DEBUG_FRONTEND_QUESTIONS_FLOW: Setting analysis data:', {
              analysisDataKeys: Object.keys(response.analysis_data),
              factorsAnalysisKeys: response.analysis_data.factors_analysis ? Object.keys(response.analysis_data.factors_analysis) : 'no factors_analysis',
              errorsDetectedCount: response.analysis_data.errors_detected?.length || 0,
              opportunitiesCount: response.analysis_data.opportunities?.length || 0
            });
            setAnalysisData(response.analysis_data);
          }
          
          setSessionId(response.session_id);
        }
      } catch (error) {
        console.error('Error loading questions:', error);
        setError('Erreur lors du chargement des questions. Veuillez réessayer.');
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    };
    
    loadQuestions();
  }, [dpeId, analysisId, questionnaireId]);
  
  // ✅ NOUVEAU : Filtrer les questions visibles quand les réponses changent
  useEffect(() => {
    if (questions.length > 0 && dpeData && analysisData) {
      console.log('=== DEBUG_FRONTEND_CONDITIONS: Starting question filtering ===');
      console.log('DEBUG_FRONTEND_CONDITIONS: Input data for filtering:', {
        totalQuestions: questions.length,
        currentAnswers: answers.length,
        hasDpeData: !!dpeData,
        hasAnalysisData: !!analysisData,
        questionIds: questions.map(q => q.id),
        dpeDataKeys: Object.keys(dpeData),
        analysisDataKeys: Object.keys(analysisData)
      });
      
      console.log('DEBUG_FRONTEND_CONDITIONS: DPE data for conditions:', {
        qualite_isolation_plancher_haut_comble_perdu: dpeData.qualite_isolation_plancher_haut_comble_perdu,
        isolation_toiture: dpeData.isolation_toiture,
        deperditions_planchers_hauts: dpeData.deperditions_planchers_hauts,
        etiquette_dpe: dpeData.etiquette_dpe,
        energy_class: dpeData.energy_class,
        annee_construction: dpeData.annee_construction,
        conso_5_usages_par_m2_ep: dpeData.conso_5_usages_par_m2_ep
      });
      
      console.log('DEBUG_FRONTEND_CONDITIONS: Analysis data for conditions:', {
        factorsAnalysis: analysisData.factors_analysis,
        errorsDetected: analysisData.errors_detected,
        opportunities: analysisData.opportunities
      });
      
      const filtered = QuestionConditionEvaluator.filterQuestions(
        questions,
        answers,
        dpeData,
        analysisData
      );
      
      console.log('DEBUG_FRONTEND_CONDITIONS: Questions after filtering:', {
        visibleQuestions: filtered.length,
        questionIds: filtered.map(q => q.id)
      });
      
      setVisibleQuestions(filtered);
      
      // Ajuster l'index actuel si la question courante n'est plus visible
      if (currentIndex >= filtered.length) {
        setCurrentIndex(Math.max(0, filtered.length - 1));
      }
    } else {
      console.log('DEBUG_FRONTEND_CONDITIONS: Cannot filter questions yet:', {
        questionsLength: questions.length,
        hasDpeData: !!dpeData,
        hasAnalysisData: !!analysisData
      });
    }
  }, [questions, answers, dpeData, analysisData, currentIndex]);
  
  // Auto-save answers when they change
  useEffect(() => {
    const saveAnswers = async () => {
      if (!sessionId || answers.length === 0) return;
      
      try {
        await DPEAPI.submitAnswers(sessionId, answers, false);
      } catch (error) {
        console.error('Error saving answers:', error);
        // Don't show error to user for auto-save
      }
    };
    
    saveAnswers();
  }, [answers, sessionId]);
  
  // Handle answer selection
  const handleAnswer = (answer: QuestionAnswer) => {
    setAnswers(prev => {
      // Check if we already have an answer for this question
      const existingIndex = prev.findIndex(a => a.question_id === answer.question_id);
      
      // Track answer
      analytics.trackEvent({
        category: 'questions',
        action: 'answer',
        label: answer.question_id,
        value: existingIndex >= 0 ? 'changed' : 'new'
      });
      
      if (existingIndex >= 0) {
        // Update existing answer
        const newAnswers = [...prev];
        newAnswers[existingIndex] = answer;
        return newAnswers;
      } else {
        // Add new answer
        return [...prev, answer];
      }
    });
  };
  
  // Navigate to next question
  const handleNext = () => {
    // ✅ NOUVELLE LOGIQUE : Utiliser les questions visibles filtrées
    const nextIndex = currentIndex + 1;
    
    if (nextIndex >= visibleQuestions.length) {
      // Plus de questions visibles, aller au résumé
      setShowSummary(true);
    } else {
      setCurrentIndex(nextIndex);
    }
  };
  
  // Navigate to previous question
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  
  // Submit all answers and complete questionnaire
  const handleSubmit = async () => {
    if (!sessionId) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Track questionnaire submission
      analytics.trackEvent({
        category: 'questions',
        action: 'submit',
        label: sessionId,
        value: answers.length
      });
      
      // Track with Meta Pixel
      metaPixel.trackQuestionnaireComplete(sessionId, answers.length);
      
      const response = await DPEAPI.submitAnswers(sessionId, answers, true);
      
      if (response.success) {
        // Track recommendations received
        analytics.trackEvent({
          category: 'questions',
          action: 'recommendations_received',
          label: sessionId
        });

        // Store recommendations — the useEffect below handles navigation
        setRecommendations(response.final_recommendations || {
          message: 'Analyse terminée',
          session_id: sessionId,
          answers_submitted: answers.length
        });
      } else {
        throw new Error(response.message || 'Erreur lors de la soumission des réponses');
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      
      // Track submission error
      analytics.trackEvent({
        category: 'questions',
        action: 'error',
        label: 'submission_error',
        error: error instanceof Error ? error.message : String(error)
      });
      
      setError('Erreur lors de la soumission des réponses. Veuillez réessayer.');
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Check if current question is answered
  const isCurrentQuestionAnswered = () => {
    if (!visibleQuestions[currentIndex]) return false;
    return answers.some(a => a.question_id === visibleQuestions[currentIndex].id);
  };
  
  // Check if all required questions are answered
  const areRequiredQuestionsAnswered = () => {
    if (!dpeData || !analysisData) return false;
    
    return QuestionConditionEvaluator.areRequiredQuestionsAnswered(
      questions,
      answers,
      dpeData,
      analysisData
    );
  };
  
  // Trigger completion after React finishes the current render cycle.
  // Must be before any conditional returns (Rules of Hooks).
  useEffect(() => {
    if (!recommendations || completionFired.current) return;
    completionFired.current = true;
    // Close any open modal before navigating to avoid DOM insertBefore errors
    setShowErrorModal(false);
    onComplete?.(recommendations);
  }, [recommendations]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Chargement des questions contextuelles..." />
      </div>
    );
  }

  if (error && !questions.length) {
    return (
      <Card className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Erreur de chargement
        </h3>
        <p className="text-gray-600 mb-6">
          {error}
        </p>
        <Button
          variant="primary"
          onClick={() => navigate(`/analysis/${dpeId}`)}
        >
          Retour à l'analyse
        </Button>
      </Card>
    );
  }
  
  return (
    <div className="max-w-3xl mx-auto">
      {/* Bandeau de valeur — visible uniquement pendant les questions, pas sur le résumé */}
      {!showSummary && (
        <div className="mb-6 rounded-xl bg-gradient-to-r from-blue-50 to-green-50 border border-blue-100 px-5 py-4">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
            À la fin de ce questionnaire, vous obtiendrez :
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-gray-700">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-green-500 shrink-0" />
              <span>Votre gain de classe DPE atteignable</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Euro className="w-4 h-4 text-green-500 shrink-0" />
              <span>Vos économies potentielles en €/an</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Hammer className="w-4 h-4 text-blue-500 shrink-0" />
              <span>La liste prioritaire des travaux à faire</span>
            </div>
          </div>
        </div>
      )}

      {/* Progress indicator */}
      <ProgressIndicator
        currentStep={showSummary ? visibleQuestions.length : currentIndex + 1}
        totalSteps={visibleQuestions.length}
        className="mb-8"
      />
      
      {/* Questions or Summary */}
      {showSummary ? (
        <QuestionsSummary
          questions={visibleQuestions}
          answers={answers}
          onEdit={(index) => {
            setCurrentIndex(index);
            setShowSummary(false);
          }}
          onSubmit={handleSubmit}
          submitting={submitting}
          canSubmit={areRequiredQuestionsAnswered()}
        />
      ) : (
        <>
          {visibleQuestions[currentIndex] && (
            <QuestionCard
              question={visibleQuestions[currentIndex]}
              onAnswer={handleAnswer}
              selectedAnswer={answers.find(a => a.question_id === visibleQuestions[currentIndex].id)}
              canGoBack={currentIndex > 0}
              onGoBack={handlePrevious}
              currentIndex={currentIndex}
              totalQuestions={visibleQuestions.length}
            />
          )}
          
          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              icon={<ArrowLeft className="w-4 h-4 mr-2" />}
            >
              Précédent
            </Button>
            
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!isCurrentQuestionAnswered() && visibleQuestions[currentIndex]?.required}
              icon={<ArrowRight className="w-4 h-4 ml-2" />}
              iconPosition="right"
            >
              {currentIndex < visibleQuestions.length - 1 ? 'Suivant' : 'Résumé'}
            </Button>
          </div>
        </>
      )}
      
      {/* Error Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Erreur"
      >
        <div className="p-4 bg-red-50 rounded-lg mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-red-800 mb-1">Une erreur est survenue</h4>
              <p className="text-sm text-red-700">
                {error}
              </p>
            </div>
          </div>
        </div>
        
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => navigate(`/analysis/${dpeId}`)}
          >
            Retour à l'analyse
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowErrorModal(false);
              window.location.reload();
            }}
          >
            Réessayer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default QuestionsFlow;